import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpload, faTrash, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useToast } from '../context/ToastContext';
import { getCustomerLogo, getCustomerLogoImageUrl, uploadCustomerLogo, deleteCustomerLogo } from '../utils/api';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // what we accept before scaling down
// The report prints the logo at most 60 x 14 mm; this is plenty for print.
const MAX_WIDTH = 1200;
const MAX_HEIGHT = 600;
// An SVG without its own size is drawn at this width.
const SVG_FALLBACK_WIDTH = 1200;

// Draw an image file onto a canvas, scaled down to fit MAX_WIDTH x MAX_HEIGHT,
// and return it as a PNG blob. This also turns SVGs into PNG, which is all the
// server takes, and keeps transparency.
const toPng = (file) => new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
                URL.revokeObjectURL(url);
                let w = img.naturalWidth || SVG_FALLBACK_WIDTH;
                let h = img.naturalHeight || Math.round(w / 3);
                const scale = Math.min(1, MAX_WIDTH / w, MAX_HEIGHT / h);
                w = Math.max(1, Math.round(w * scale));
                h = Math.max(1, Math.round(h * scale));
                const canvas = document.createElement('canvas');
                canvas.width = w;
                canvas.height = h;
                canvas.getContext('2d').drawImage(img, 0, 0, w, h);
                canvas.toBlob(blob => (blob ? resolve(blob) : reject(new Error('empty render'))), 'image/png');
        };
        img.onerror = () => {
                URL.revokeObjectURL(url);
                reject(new Error('could not load image'));
        };
        img.src = url;
});

// ReportLogo lets a facility manager put their own logo at the top of the
// monthly report. Cleverplant's logo then moves to the footer.
const ReportLogo = ({ customerId }) => {
        const { showToast } = useToast();
        const [logo, setLogo] = useState(null);
        const [loading, setLoading] = useState(true);
        const [busy, setBusy] = useState(false);
        const [error, setError] = useState('');
        const inputRef = useRef(null);

        useEffect(() => {
                let cancelled = false;
                const load = async () => {
                        try {
                                const res = await getCustomerLogo(customerId);
                                if (cancelled) return;
                                if (res.ok) {
                                        setLogo((await res.json()).data);
                                } else if (res.status !== 404) {
                                        throw new Error(`status ${res.status}`);
                                }
                        } catch (e) {
                                console.error('Failed to load logo', e);
                        } finally {
                                if (!cancelled) setLoading(false);
                        }
                };
                load();
                return () => { cancelled = true; };
        }, [customerId]);

        const handleFile = async (file) => {
                setError('');
                if (!file) return;
                if (!['image/png', 'image/jpeg', 'image/svg+xml'].includes(file.type)) {
                        setError('Please select a PNG, JPEG or SVG file.');
                        return;
                }
                if (file.size > MAX_FILE_SIZE) {
                        setError('File is too large. Maximum size is 10MB.');
                        return;
                }
                setBusy(true);
                try {
                        const png = await toPng(file);
                        const formData = new FormData();
                        formData.append('file', png, 'logo.png');
                        const res = await uploadCustomerLogo(customerId, formData);
                        if (!res.ok) {
                                const body = await res.json().catch(() => ({}));
                                setError(body.message || 'Could not save the logo.');
                                return;
                        }
                        setLogo((await res.json()).data);
                        showToast('success', 'Logo saved. It will be on your next report.');
                } catch (e) {
                        console.error('Failed to upload logo', e);
                        setError('Could not read this image. Try another file.');
                } finally {
                        setBusy(false);
                        if (inputRef.current) inputRef.current.value = '';
                }
        };

        const handleRemove = async () => {
                setBusy(true);
                try {
                        const res = await deleteCustomerLogo(customerId);
                        if (!res.ok) throw new Error(`status ${res.status}`);
                        setLogo(null);
                        showToast('success', 'Logo removed. Reports will show the Cleverplant logo.');
                } catch (e) {
                        showToast('error', 'Could not remove the logo.');
                } finally {
                        setBusy(false);
                }
        };

        return (
                <section className="bg-white/60 backdrop-blur-sm rounded-2xl shadow-card p-6 mb-8" data-testid="report-logo">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-6">
                                <div className="w-full sm:w-64 h-24 shrink-0 rounded-xl border border-gray-200 bg-white flex items-center justify-center p-3">
                                        {loading ? (
                                                <FontAwesomeIcon icon={faSpinner} spin className="text-gray-400" />
                                        ) : logo ? (
                                                <img
                                                        src={getCustomerLogoImageUrl(customerId, logo.updated_at)}
                                                        alt="Your report logo"
                                                        data-testid="report-logo-preview"
                                                        className="max-w-full max-h-full object-contain"
                                                />
                                        ) : (
                                                <img src="/images/logo_text_color.png" alt="Cleverplant logo" data-testid="report-logo-default" className="max-w-full max-h-12 object-contain opacity-80" />
                                        )}
                                </div>
                                <div className="min-w-0 flex-1">
                                        <h2 className="font-semibold text-text-strong">Report logo</h2>
                                        <p className="text-sm text-text-weak mt-1">
                                                {logo
                                                        ? 'Your logo is at the top of your reports, with the Cleverplant logo in the footer.'
                                                        : 'Add your company logo to the top of your reports. The Cleverplant logo moves to the footer.'}
                                                {' '}It applies from the next report; reports already made stay as they are.
                                        </p>
                                        <div className="flex flex-wrap gap-3 mt-4">
                                                <input
                                                        ref={inputRef}
                                                        type="file"
                                                        accept="image/png,image/jpeg,image/svg+xml"
                                                        data-testid="report-logo-input"
                                                        onChange={(e) => handleFile(e.target.files?.[0])}
                                                        className="hidden"
                                                />
                                                <button
                                                        type="button"
                                                        onClick={() => inputRef.current?.click()}
                                                        disabled={busy || loading}
                                                        data-testid="report-logo-upload"
                                                        className="inline-flex items-center px-4 py-2 bg-brand text-white rounded-xl hover:bg-brand/90 transition-colors disabled:opacity-60"
                                                >
                                                        <FontAwesomeIcon icon={busy ? faSpinner : faUpload} spin={busy} className="mr-2" />
                                                        {logo ? 'Replace logo' : 'Upload logo'}
                                                </button>
                                                {logo && (
                                                        <button
                                                                type="button"
                                                                onClick={handleRemove}
                                                                disabled={busy}
                                                                data-testid="report-logo-remove"
                                                                className="inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-gray-700 rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-60"
                                                        >
                                                                <FontAwesomeIcon icon={faTrash} className="mr-2" />
                                                                Remove
                                                        </button>
                                                )}
                                        </div>
                                        <p className="text-xs text-gray-500 mt-2">PNG, JPEG or SVG. A transparent background looks best.</p>
                                        {error && <p data-testid="report-logo-error" className="text-status-critical text-sm mt-2">{error}</p>}
                                </div>
                        </div>
                </section>
        );
};

export default ReportLogo;
